import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { Flex, extraSmall } from '@ubeya/shared-web/components';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';

const Wrapper = styled.div`
  margin-top: 60px;
`;

const Container = styled(Flex)`
  width: 100%;
  height: 10px;
  background: ${({ theme }) => theme.colors.white};
  position: relative;
  box-shadow: 0 2px 20px 0 rgba(47, 60, 79, 0.12);
`;

const WorkingTime = styled.div`
  position: absolute;
  background-image: linear-gradient(
    to right,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.primary}a0
  );
  height: 10px;
  width: ${({ width }) => width}%;
  left: ${({ left = 0 }) => left}%;
  border-radius: 10px;

  &:before,
  &:after {
    position: absolute;
    top: -18px;
    ${extraSmall};
    background: ${({ theme }) => theme.colors.white};
  }

  &:before {
    content: '${({ startTime }) => startTime}';
    left: 2px;
  }

  &:after {
    content: '${({ endTime }) => endTime}';
    right: 2px;
  }
`;

const Circle = styled.div`
  width: 6px;
  height: 6px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 100%;
  position: absolute;
  top: 2px;
  ${({ isStart }) => (isStart ? 'left' : 'right')}:2px;
`;

const getDuration = (timeA, timeB) => moment(timeA).diff(timeB, 'minutes');

const BreaksLine = ({ startTime, endTime }) => {
  const { timeFormat } = useContext(ConfigContext);
  const { data: breaks } = useArrayManagement('breaktimes');

  const getFormattedTime = useCallback((time) => moment(time).format(timeFormat), [timeFormat]);

  const workingTimes = useMemo(() => {
    if (!startTime || !endTime) {
      return [];
    }

    const working = [];
    let start = startTime;
    breaks
      .filter(
        (breakTime) =>
          moment(breakTime.startTime).isSameOrAfter(startTime) && moment(breakTime.endTime).isSameOrBefore(endTime)
      )
      .sort(
        (a, b) => parseInt(moment(a.startTime).format('HHmm'), 10) - parseInt(moment(b.startTime).format('HHmm'), 10)
      )
      .forEach((breakData) => {
        working.push([start, breakData.startTime]);
        start = breakData.endTime;
      });

    working.push([start, endTime]);

    return working;
  }, [breaks, endTime, startTime]);

  if (!startTime || !endTime) {
    return null;
  }

  const total = getDuration(endTime, startTime);

  return (
    <Wrapper>
      <Container>
        {workingTimes.map(([start, end], index) => {
          const width = (getDuration(end, start) * 100) / total;
          const left = (getDuration(start, startTime) * 100) / total;
          const formattedStartTime = getFormattedTime(start);
          const formattedEndTime = getFormattedTime(end);
          return (
            <WorkingTime
              key={index}
              width={width}
              left={left}
              startTime={formattedStartTime}
              endTime={formattedEndTime}>
              <Circle isStart />
              <Circle />
            </WorkingTime>
          );
        })}
      </Container>
    </Wrapper>
  );
};

export default BreaksLine;
